import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, loadRazorpay } from "@/lib/api";
import { toast } from "sonner";

const PLANS = {
  individual_inr: { label: "Individual · India", currency: "INR" },
  individual_usd: { label: "Individual · International", currency: "USD" },
  institution_inr: { label: "Institutional · India (slab)", currency: "INR" },
  institution_usd: { label: "Institutional · International", currency: "USD" },
};

export default function Checkout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initialPlan = params.get("plan") || "individual_inr";
  const [plan, setPlan] = useState(initialPlan);
  const [quantity, setQuantity] = useState(plan.startsWith("institution") ? 50 : 1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/public/settings").then((r) => setSettings(r.data)); }, []);

  const isInst = plan.startsWith("institution");

  const computeAmount = () => {
    if (plan === "individual_inr") return { amount: settings.individual_price_inr || 360, currency: "INR", qty: 1 };
    if (plan === "individual_usd") return { amount: settings.individual_price_usd || 9, currency: "USD", qty: 1 };
    if (plan === "institution_inr") {
      const slabs = settings.pricing_slabs_inr || [];
      const slab = slabs.find(([lo, hi]) => quantity >= lo && quantity <= hi) || [0, 0, 310];
      return { amount: slab[2] * quantity, currency: "INR", qty: quantity, unit: slab[2] };
    }
    if (plan === "institution_usd") return { amount: (settings.institutional_price_usd || 9) * quantity, currency: "USD", qty: quantity, unit: settings.institutional_price_usd || 9 };
    return { amount: 0, currency: "INR", qty: 1 };
  };
  const calc = computeAmount();

  const pay = async (e) => {
    e.preventDefault();
    if (!name || !email) { toast.error("Please fill all required fields."); return; }
    setLoading(true);
    try {
      const order = await api.post("/payments/create-order", {
        plan, quantity, name, email, phone, institution_name: institutionName,
      });
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Failed to load Razorpay."); setLoading(false); return; }
      const rz = new window.Razorpay({
        key: order.data.key_id,
        amount: order.data.amount,
        currency: order.data.currency,
        name: "MSIS",
        description: PLANS[plan].label,
        order_id: order.data.order_id,
        prefill: { name, email, contact: phone },
        theme: { color: "#002FA7" },
        handler: async (resp) => {
          try {
            const verify = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful! Test ID emailed.");
            sessionStorage.setItem("msis_purchase", JSON.stringify(verify.data));
            nav("/?paid=1");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rz.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="container-msis py-12 max-w-4xl">
        <div className="overline mb-3">Checkout</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Complete your subscription.</h1>
        <div className="grid lg:grid-cols-2 gap-10 mt-10">
          <form onSubmit={pay} className="space-y-4" data-testid="checkout-form">
            <div>
              <Label>Plan</Label>
              <select className="w-full border border-border rounded-md p-2 mt-1 bg-white"
                value={plan} onChange={(e) => { setPlan(e.target.value); setQuantity(e.target.value.startsWith("institution") ? 50 : 1); }}
                data-testid="plan-select">
                {Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            {isInst && (
              <>
                <div>
                  <Label>Institution Name</Label>
                  <Input data-testid="institution-name-input" value={institutionName} onChange={e => setInstitutionName(e.target.value)} placeholder="e.g. ABC Business School" />
                </div>
                <div>
                  <Label>Quantity (number of tests)</Label>
                  <Input data-testid="quantity-input" type="number" min={1} value={quantity} onChange={e => setQuantity(parseInt(e.target.value || "1"))} />
                </div>
              </>
            )}
            <div>
              <Label>Contact Name</Label>
              <Input data-testid="checkout-name" value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="checkout-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input data-testid="checkout-phone" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="pay-btn">
              {loading ? "Processing…" : `Pay ${calc.currency === "INR" ? "₹" : "$"}${calc.amount.toLocaleString()}`}
            </button>
            <div className="text-xs text-slate2">Secured by Razorpay. By proceeding you agree to our <a href="/privacy" className="underline">Terms</a>.</div>
          </form>
          <aside className="border border-border rounded-md p-6 h-fit">
            <div className="overline">Order Summary</div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate2">Plan</span><span>{PLANS[plan].label}</span></div>
              <div className="flex justify-between"><span className="text-slate2">Quantity</span><span className="font-mono">{calc.qty}</span></div>
              {calc.unit && <div className="flex justify-between"><span className="text-slate2">Unit price</span><span className="font-mono">{calc.currency === "INR" ? "₹" : "$"}{calc.unit}</span></div>}
            </div>
            <div className="border-t border-border mt-4 pt-4 flex justify-between items-baseline">
              <span className="text-slate2">Total</span>
              <span className="font-display font-bold text-2xl">{calc.currency === "INR" ? "₹" : "$"}{calc.amount.toLocaleString()}</span>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
